/**
 * InvestorAdminDashboard — view who's opened the /overview link, detect
 * forwarded shares, revoke, and create new per-investor keys.
 *
 * Route: /overview/admin?admin_key=<INVESTOR_ADMIN_KEY>
 *
 * Gated server-side via /api/investor/admin/verify. Wrong/missing key → 404.
 *
 * Pulls from /api/investor/admin/keys (rows + alerts), supports:
 *   - inline create (recipient → returns shareable URL)
 *   - revoke / restore
 *   - expand row → see every open event (timestamp, city, browser)
 *   - dismiss forward-detection alert
 *   - copy each shareable URL with one click
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, AlertTriangle, Plus, Copy, Check, ChevronDown, ChevronUp,
  ShieldAlert, ShieldCheck, RotateCcw, X, Globe2, Users, Eye,
  Sparkles, ExternalLink, RefreshCw, Trash2,
} from 'lucide-react';
import SspStatusPill from '../components/investor/SspStatusPill';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_OVERVIEW_BASE || window.location.origin;

export default function InvestorAdminDashboard() {
  const [params] = useSearchParams();
  const adminKey = params.get('admin_key') || '';
  const [state, setState] = useState('checking'); // checking | allow | deny

  useEffect(() => {
    if (!adminKey) { setState('deny'); return; }
    axios.get(`${API}/api/investor/admin/verify`, {
      params: { admin_key: adminKey }, validateStatus: () => true,
    }).then(({ status, data }) => {
      setState(status === 200 && data?.valid ? 'allow' : 'deny');
    }).catch(() => setState('deny'));
  }, [adminKey]);

  if (state === 'checking') {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] flex items-center justify-center" data-testid="admin-checking">
        <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
      </div>
    );
  }
  if (state === 'deny') {
    return (
      <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="admin-404">
        <div className="max-w-sm text-center space-y-3">
          <h1 className="text-7xl font-black tracking-tight text-gray-700">404</h1>
          <p className="text-sm text-gray-500">This page doesn&apos;t exist.</p>
          <Link to="/login" className="inline-block mt-4 text-xs text-gray-600 hover:text-gray-300">Return home</Link>
        </div>
      </div>
    );
  }
  return <AdminBody adminKey={adminKey} />;
}

function AdminBody({ adminKey }) {
  const [data, setData] = useState({ keys: [], alerts: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [opens, setOpens] = useState({}); // { [key]: opens[] }
  const [creating, setCreating] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [error, setError] = useState('');
  // Demo Studio nav + re-seed state
  const [demoPath, setDemoPath] = useState('');     // e.g. /demo?key=<presenter-key>
  const [reseeding, setReseeding] = useState(false);
  const [reseedResult, setReseedResult] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [embeddingsResult, setEmbeddingsResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data: payload } = await axios.get(`${API}/api/investor/admin/keys`, {
        params: { admin_key: adminKey },
      });
      setData(payload);
    } catch (e) {
      setError('Failed to load investor keys.');
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  // One-shot fetch of the founder-only Demo Studio URL.
  const fetchDemoLink = useCallback(async () => {
    try {
      const { data: payload } = await axios.get(`${API}/api/investor/admin/demo-link`, {
        params: { admin_key: adminKey },
      });
      if (payload?.configured && payload?.path) setDemoPath(payload.path);
    } catch (e) { /* silent — it's a nice-to-have */ }
  }, [adminKey]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { fetchDemoLink(); }, [fetchDemoLink]);

  const reseedDemo = async () => {
    if (reseeding) return;
    setReseeding(true);
    setReseedResult(null);
    setError('');
    try {
      const { data: payload } = await axios.post(
        `${API}/api/investor/admin/reseed-demo`, null,
        { params: { admin_key: adminKey } },
      );
      setReseedResult(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Demo re-seed failed.');
    } finally {
      setReseeding(false);
    }
  };

  const rebuildEmbeddings = async () => {
    if (rebuilding) return;
    setRebuilding(true);
    setEmbeddingsResult(null);
    setError('');
    try {
      const { data: payload } = await axios.post(
        `${API}/api/investor/admin/rebuild-embeddings`, null,
        { params: { admin_key: adminKey }, timeout: 120_000 },
      );
      setEmbeddingsResult(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Embeddings rebuild failed.');
    } finally {
      setRebuilding(false);
    }
  };

  const loadOpens = async (key) => {
    try {
      const { data: payload } = await axios.get(
        `${API}/api/investor/admin/keys/${encodeURIComponent(key)}/opens`,
        { params: { admin_key: adminKey } },
      );
      setOpens((prev) => ({ ...prev, [key]: payload.opens }));
    } catch (e) {
      setError('Failed to load open events.');
    }
  };

  const deleteOpen = async (key, sessionId) => {
    try {
      await axios.delete(
        `${API}/api/investor/admin/keys/${encodeURIComponent(key)}/opens/${encodeURIComponent(sessionId)}`,
        { params: { admin_key: adminKey } },
      );
      // Optimistic local update + refresh the key roll-up so sessions /
      // cities / forwarded-flag re-compute.
      setOpens((prev) => ({
        ...prev,
        [key]: (prev[key] || []).filter((o) => o.session_id !== sessionId),
      }));
      refresh();
    } catch (e) {
      setError('Failed to delete session.');
    }
  };

  const toggleExpand = (key) => {
    if (expanded === key) {
      setExpanded(null);
    } else {
      setExpanded(key);
      if (!opens[key]) loadOpens(key);
    }
  };

  const createKey = async () => {
    if (!newRecipient.trim()) return;
    setCreating(true); setError('');
    try {
      await axios.post(
        `${API}/api/investor/admin/keys`,
        { recipient: newRecipient.trim() },
        { params: { admin_key: adminKey } },
      );
      setNewRecipient('');
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not create key.');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key, restore = false) => {
    try {
      await axios.post(
        `${API}/api/investor/admin/keys/${encodeURIComponent(key)}/${restore ? 'restore' : 'revoke'}`,
        {},
        { params: { admin_key: adminKey } },
      );
      await refresh();
    } catch (e) {
      setError('Could not update key.');
    }
  };

  const dismissAlert = async (key) => {
    try {
      await axios.delete(`${API}/api/investor/admin/alerts/${encodeURIComponent(key)}`, {
        params: { admin_key: adminKey },
      });
      await refresh();
    } catch (e) {
      setError('Could not dismiss alert.');
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        {/* Header */}
        <header className="flex items-start justify-between gap-4">
          <div className="space-y-2 flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black text-[#FF4D94]">Investor Tracking</p>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight">Who&rsquo;s opening the deck?</h1>
            <p className="text-sm text-gray-500 max-w-2xl">
              Each investor gets their own URL. We log every open with IP, city, browser, and a long-lived
              session cookie so &ldquo;same person reloading&rdquo; is told apart from &ldquo;forwarded.&rdquo;
            </p>
          </div>
          {/* SSP integration health pill — iter-92.9.4. Includes the new
              RESCUE chip that surfaces brand_embedding retry-rescue rate
              (0% good / 8% warning / 25% incident). Both DSP and SSP teams
              watch the same canary. */}
          <div className="shrink-0 pt-1" data-testid="admin-ssp-health">
            <SspStatusPill />
          </div>
        </header>

        {/* ─── Demo Studio quick-launch (founder-only) ─── */}
        <div className="rounded-xl border border-[#00D4FF]/20 bg-gradient-to-br from-[#0d1c22] to-[#0a1517] p-5 flex flex-col sm:flex-row gap-4 sm:items-center"
             data-testid="admin-demo-panel">
          <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#00D4FF]/10 text-[#00D4FF]">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#00D4FF]">Demo Studio</p>
              <p className="text-sm text-white font-medium mt-0.5">Open the 12-brand investor walkthrough</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Bookmark the link below — opens in a new tab so it doesn&rsquo;t kill this dashboard.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            {demoPath ? (
              <a
                href={demoPath}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-open-demo"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#00D4FF] text-[#0A0A0A] text-[12px] font-black uppercase tracking-wider hover:brightness-110 transition"
              >
                Open Demo Studio
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : (
              <span className="text-[11px] text-gray-500" data-testid="admin-demo-unconfigured">
                DEMO_PRESENTER_KEY not configured on server
              </span>
            )}
            <button
              onClick={reseedDemo}
              disabled={reseeding}
              data-testid="admin-reseed-demo"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-[#1F1F1F] bg-[#0A0A0A] text-gray-300 text-[12px] font-bold uppercase tracking-wider hover:border-[#00D4FF]/50 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              title="Wipes + re-creates the 12 demo brands, waves, and creator rosters in this database. Safe to run after each deploy."
            >
              {reseeding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {reseeding ? 'Re-seeding…' : 'Re-seed demo data'}
            </button>
            <button
              onClick={rebuildEmbeddings}
              disabled={rebuilding}
              data-testid="admin-rebuild-embeddings"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-[#1F1F1F] bg-[#0A0A0A] text-gray-300 text-[12px] font-bold uppercase tracking-wider hover:border-[#FF4D94]/50 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              title="Re-runs the v2 audience-signal embeddings pipeline. Recomputes all 300 creators' 384-d vectors and per-niche centroids. Takes ~30s."
            >
              {rebuilding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {rebuilding ? 'Rebuilding…' : 'Rebuild embeddings'}
            </button>
          </div>
        </div>

        {reseedResult && (
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-[12px] text-emerald-300"
               data-testid="admin-reseed-result">
            <span className="font-medium">Re-seed complete:</span>{' '}
            {reseedResult.brands_seeded} brands · {reseedResult.skus_seeded} SKUs ·
            {' '}{reseedResult.waves_seeded} waves ·
            {' '}{reseedResult.creator_assignments_seeded} creator assignments
          </div>
        )}

        {embeddingsResult && (
          <div className="rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/5 px-4 py-3 text-[12px] text-fuchsia-300"
               data-testid="admin-embeddings-result">
            <span className="font-medium">Embeddings rebuild complete:</span>{' '}
            {embeddingsResult.creators_embedded} of {embeddingsResult.creators_total} creators embedded ·
            {' '}{embeddingsResult.centroids_written} niche centroids written
          </div>
        )}

        {error && (
          <div className="rounded-xl px-4 py-3 bg-[#3a1818] border border-[#FF4D94]/40 text-sm text-[#FF4D94]" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Forward-detection alerts */}
        {data.alerts.length > 0 && (
          <div className="rounded-xl border border-[#FF4D94]/40 bg-gradient-to-br from-[#2a1118] to-[#1a0d12] p-5 space-y-3" data-testid="admin-alerts">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-[#FF4D94]" />
              <p className="text-[11px] uppercase tracking-[0.22em] font-black text-[#FF4D94]">
                {data.alerts.length} link{data.alerts.length === 1 ? '' : 's'} appears to be forwarded
              </p>
            </div>
            <div className="space-y-1.5">
              {data.alerts.map((a) => (
                <div key={a.key} className="flex items-center justify-between gap-3 text-[13px]">
                  <span className="font-bold text-white">{a.recipient}</span>
                  <span className="text-gray-400 text-[12px] flex-1 truncate">{a.reason}</span>
                  <button onClick={() => dismissAlert(a.key)}
                          className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-white transition flex items-center gap-1"
                          data-testid={`admin-dismiss-${a.key}`}>
                    <X className="h-3 w-3" /> dismiss
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create new key */}
        <div className="rounded-xl bg-[#141414] border border-[#1F1F1F] p-5" data-testid="admin-create">
          <p className="text-[11px] uppercase tracking-[0.22em] font-black text-[#00D4FF] mb-3">Issue a new tracked link</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="admin-create-input"
              type="text"
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createKey(); }}
              placeholder="Recipient name (e.g., Sequoia / Roelof)"
              className="flex-1 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#00D4FF]/50"
            />
            <button onClick={createKey} disabled={creating || !newRecipient.trim()}
                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#00D4FF] text-[#0A0A0A] text-[12px] font-black uppercase tracking-wider hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    data-testid="admin-create-btn">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Generate link
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl bg-[#141414] border border-[#1F1F1F] overflow-hidden" data-testid="admin-table">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
            </div>
          ) : data.keys.length === 0 ? (
            <div className="p-12 text-center text-sm text-gray-500">No tracked links yet. Create one above.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#0F0F0F] text-[10px] uppercase tracking-[0.18em] text-gray-500">
                  <th className="text-left py-3 px-4 font-bold">Recipient</th>
                  <th className="text-left py-3 px-2 font-bold">Opens</th>
                  <th className="text-left py-3 px-2 font-bold">Sessions</th>
                  <th className="text-left py-3 px-2 font-bold">Cities</th>
                  <th className="text-left py-3 px-2 font-bold">Time on page</th>
                  <th className="text-left py-3 px-2 font-bold">Last open</th>
                  <th className="text-left py-3 px-2 font-bold">Status</th>
                  <th className="text-right py-3 px-4 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.keys.map((row) => (
                  <KeyRow key={row.key} row={row} expanded={expanded === row.key}
                          opens={opens[row.key]}
                          onToggle={() => toggleExpand(row.key)}
                          onRevoke={() => revoke(row.key, false)}
                          onRestore={() => revoke(row.key, true)}
                          onDeleteOpen={(sid) => deleteOpen(row.key, sid)} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-[10px] text-gray-600 text-center pt-4">
          Auto-revoke kicks in at 4+ distinct sessions or 4+ distinct cities per key. Forwarded-flag fires at 3+. Time on page only counts attentive seconds (visible + focused).
        </p>
      </div>
    </div>
  );
}

function KeyRow({ row, expanded, opens, onToggle, onRevoke, onRestore, onDeleteOpen }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const url = `${PUBLIC_BASE}/overview?key=${row.key}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyError('');
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard API can fail in non-https or sandboxed iframes — surface
      // it so the user knows to copy by hand instead of thinking it worked.
      setCopyError('Clipboard blocked — copy manually');
      setTimeout(() => setCopyError(''), 2500);
    }
  };
  const lastOpen = useFmtDate(row.last_open);
  const cityList = row.cities.length === 0 ? '—' :
                   row.cities.length <= 2 ? row.cities.join(' · ') :
                   `${row.cities.slice(0, 2).join(' · ')} +${row.cities.length - 2}`;
  // Format time on page (attentive seconds) — "—" before any heartbeat,
  // "Xm Ys" for short reads, "Xh Ym" if a VC really sat with the deck.
  const secs = row.total_seconds_on_page || 0;
  const timeOnPage = secs === 0 ? '—'
    : secs < 60       ? `${secs}s`
    : secs < 3600     ? `${Math.floor(secs / 60)}m ${secs % 60}s`
    :                   `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;

  return (
    <>
      <tr className="border-t border-[#1F1F1F] text-[13px]" data-testid={`admin-row-${row.key}`}>
        <td className="py-3 px-4">
          <button onClick={onToggle}
                  className="flex items-center gap-2 text-left hover:text-[#00D4FF] transition"
                  data-testid={`admin-row-toggle-${row.key}`}>
            {expanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-500" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-500" />}
            <span className="font-black text-white">{row.recipient}</span>
          </button>
        </td>
        <td className="py-3 px-2 font-bold">{row.opens}</td>
        <td className="py-3 px-2 font-bold flex items-center gap-1.5">
          <Users className="h-3 w-3 text-gray-500" /> {row.sessions}
        </td>
        <td className="py-3 px-2 text-gray-400 text-[12px]">{cityList}</td>
        <td className="py-3 px-2 text-gray-300 text-[12px] font-bold tabular-nums" data-testid={`admin-row-time-${row.key}`}>
          {timeOnPage}
        </td>
        <td className="py-3 px-2 text-gray-400 text-[12px]">{lastOpen || '—'}</td>
        <td className="py-3 px-2">
          {row.auto_revoked ? (
            <span className="text-[10px] uppercase tracking-wider font-black text-[#FF4D94] flex items-center gap-1" title={row.auto_revoke_reason || ''}>
              <ShieldAlert className="h-3 w-3" /> Auto-Revoked
            </span>
          ) : row.revoked ? (
            <span className="text-[10px] uppercase tracking-wider font-black text-gray-500">Revoked</span>
          ) : row.forwarded ? (
            <span className="text-[10px] uppercase tracking-wider font-black text-[#FF4D94] flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> Forwarded
            </span>
          ) : row.opens === 0 ? (
            <span className="text-[10px] uppercase tracking-wider font-black text-gray-500">Unopened</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider font-black text-[#34D399] flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Single viewer
            </span>
          )}
        </td>
        <td className="py-3 px-4 text-right">
          <div className="flex items-center justify-end gap-2">
            <button onClick={copy} className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-white inline-flex items-center gap-1 transition"
                    data-testid={`admin-copy-${row.key}`}
                    title={copyError || ''}>
              {copied ? <Check className="h-3 w-3 text-[#34D399]" /> : <Copy className="h-3 w-3" />}
              {copyError ? 'copy failed' : copied ? 'copied' : 'copy URL'}
            </button>
            {row.revoked ? (
              <button onClick={onRestore} className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-white inline-flex items-center gap-1"
                      data-testid={`admin-restore-${row.key}`}>
                <RotateCcw className="h-3 w-3" /> restore
              </button>
            ) : (
              <button onClick={onRevoke} className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-[#FF4D94] inline-flex items-center gap-1"
                      data-testid={`admin-revoke-${row.key}`}>
                <X className="h-3 w-3" /> revoke
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#0F0F0F] border-t border-[#1F1F1F]" data-testid={`admin-expand-${row.key}`}>
          <td colSpan={7} className="px-4 py-4">
            <div className="space-y-2 text-[12px] text-gray-400 mb-3">
              <div className="flex items-center gap-2"><Eye className="h-3 w-3" />URL: <code className="text-[#00D4FF]">{url}</code></div>
              {row.auto_revoke_reason && (
                <div className="flex items-center gap-2 text-[#FF4D94]" data-testid={`admin-auto-revoke-reason-${row.key}`}>
                  <ShieldAlert className="h-3 w-3" />Auto-revoked: {row.auto_revoke_reason}
                </div>
              )}
              {row.forwarded_reason && (
                <div className="flex items-center gap-2 text-[#FF4D94]"><ShieldAlert className="h-3 w-3" />Forward signal: {row.forwarded_reason}</div>
              )}
            </div>
            {!opens ? (
              <div className="py-4 flex items-center gap-2 text-gray-500 text-[12px]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading opens...
              </div>
            ) : opens.length === 0 ? (
              <p className="text-[12px] text-gray-600 py-3">No opens recorded yet.</p>
            ) : (
              <div className="rounded-lg border border-[#1F1F1F] divide-y divide-[#1F1F1F]">
                {opens.slice(0, 25).map((op) => (
                  <OpenRow key={`${op.ts}-${op.session_id}-${op.ip}`}
                           op={op}
                           onDelete={() => op.session_id && onDeleteOpen(op.session_id)} />
                ))}
                {opens.length > 25 && (
                  <p className="text-[10px] text-gray-600 text-center py-2">Showing 25 of {opens.length} opens</p>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function OpenRow({ op, onDelete }) {
  const ts = useFmtDate(op.ts, true);
  const loc = [op.city, op.region, op.country].filter(Boolean).join(', ') || '—';
  const browser = parseBrowser(op.ua);
  const canDelete = Boolean(op.session_id);
  return (
    <div className="px-3 py-2 grid grid-cols-12 gap-2 text-[11.5px] items-center group">
      <span className="col-span-3 text-gray-400">{ts}</span>
      <span className="col-span-3 text-white flex items-center gap-1.5"><Globe2 className="h-3 w-3 text-gray-600" />{loc}</span>
      <span className="col-span-2 text-gray-500">{op.ip || '—'}</span>
      <span className="col-span-2 text-gray-500 truncate" title={op.ua}>{browser}</span>
      <span className="col-span-1 text-gray-700 font-mono text-[10px] text-right" title={op.session_id}>
        {op.session_id ? op.session_id.slice(0, 6) : '—'}
      </span>
      <span className="col-span-1 flex justify-end">
        {canDelete && (
          <button
            onClick={onDelete}
            title="Delete this tracked session"
            data-testid={`admin-open-delete-${op.session_id}`}
            className="text-gray-700 hover:text-[#FF4D94] transition opacity-60 hover:opacity-100"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}

// ── small utils ──────────────────────────────────────────────────────────
function useFmtDate(iso, withTime = false) {
  return useMemo(() => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    if (!withTime) return date;
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${date} · ${time}`;
  }, [iso, withTime]);
}

function parseBrowser(ua) {
  if (!ua) return '—';
  if (/iPhone|iPad/.test(ua)) return /Safari/.test(ua) ? 'iOS Safari' : 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Safari/.test(ua)) return 'Safari';
  return ua.split(' ')[0].slice(0, 28);
}

